import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../App';
import { useAuth } from '../App';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion', all: 'All Bookmakers',
};

const PROMO_TYPES = {
  welcome: 'Welcome Bonus', reload: 'Reload Bonus', cashback: 'Cashback',
  free_bet: 'Free Bet', odds_boost: 'Odds Boost', other: 'Other',
};

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card" style={{ '--accent-color': color || 'var(--accent)' }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value ?? '—'}</div>
    </div>
  );
}

function DailyParlayAdmin() {
  const [parlays, setParlays] = useState([]);
  const [building, setBuilding] = useState(false);
  const [settling, setSettling] = useState(null);

  useEffect(() => {
    loadParlays();
  }, []);

  async function loadParlays() {
    try {
      const data = await apiFetch('/daily-parlay/history?limit=14');
      setParlays(data.history || []);
    } catch (err) { console.error(err); }
  }

  async function buildToday() {
    setBuilding(true);
    try {
      await apiFetch('/daily-parlay/build', { method: 'POST' });
      await loadParlays();
      alert('Daily parlays built successfully!');
    } catch (err) { alert('Failed to build: ' + err.message); }
    finally { setBuilding(false); }
  }

  async function settle(id, won) {
    if (!confirm(`Mark this parlay as ${won ? 'WON' : 'LOST'}?`)) return;
    setSettling(id);
    try {
      await apiFetch(`/daily-parlay/${id}/settle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ won }),
      });
      await loadParlays();
    } catch (err) { alert('Failed to settle: ' + err.message); }
    finally { setSettling(null); }
  }

  const STATUS_COLOR = { pending: 'var(--text3)', won: 'var(--success)', lost: 'var(--danger)', void: 'var(--warning)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={buildToday} disabled={building}>
          {building ? 'Building...' : '🃏 Build Today\'s Parlays'}
        </button>
        <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={loadParlays}>
          ↺ Refresh
        </button>
      </div>

      {parlays.length === 0 ? (
        <div className="empty-state"><p>No daily parlays yet.</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {parlays.map(p => (
            <div key={p._id} style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{p.date}</span>
                    <span className={`pill ${p.mode === 'pro' ? 'pill-green' : 'pill-blue'}`} style={{ fontSize: 9 }}>
                      {p.mode === 'pro' ? '⭐ PRO' : 'BASIC'}
                    </span>
                    <span style={{ fontSize: 11, color: STATUS_COLOR[p.status], fontWeight: 600 }}>
                      {p.status.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>
                    {p.legs?.length} legs · {p.combinedOdds}x · P{p.potentialReturn} potential
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {p.legs?.map((leg, i) => (
                      <div key={i} style={{ fontSize: 11, color: 'var(--text2)' }}>
                        {i + 1}. {leg.match} — {leg.market} {leg.selection} @ {leg.odds}
                        {leg.result !== 'pending' && (
                          <span style={{ marginLeft: 6, color: leg.result === 'won' ? 'var(--success)' : 'var(--danger)' }}>
                            {leg.result === 'won' ? '✅' : '❌'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {p.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: 11, color: 'var(--success)' }}
                      onClick={() => settle(p._id, true)}
                      disabled={settling === p._id}
                    >
                      ✅ Won
                    </button>
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: 11, color: 'var(--danger)' }}
                      onClick={() => settle(p._id, false)}
                      disabled={settling === p._id}
                    >
                      ❌ Lost
                    </button>
                  </div>
                )}
                {p.status !== 'pending' && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: STATUS_COLOR[p.status] }}>
                      {p.status === 'won' ? `+P${p.actualReturn}` : `-P${p.stake}`}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [userPlan, setUserPlan] = useState('ALL');
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    bookmaker: 'betway_bw', title: '', description: '', howToUse: '',
    promoType: 'welcome', value: '', minDeposit: '', expiresAt: '',
  });
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const data = await apiFetch('/admin/stats');
      setStats(data);
    } catch (err) { console.error(err); }
  }, []);

  // Load users
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 20 });
      if (userSearch) params.set('search', userSearch);
      if (userPlan !== 'ALL') params.set('plan', userPlan);
      const data = await apiFetch(`/admin/users?${params}`);
      setUsers(data.users || []);
      setUserTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [userSearch, userPlan]);

  // Load promotions
  const loadPromotions = useCallback(async () => {
    try {
      const data = await apiFetch('/admin/promotions');
      setPromotions(data.promotions || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    loadStats();
    loadPromotions();
  }, [loadStats, loadPromotions]);

  useEffect(() => {
    if (tab === 'users') loadUsers();
  }, [tab, loadUsers]);

  // Update user plan
  async function updateUser(id, updates) {
    try {
      await apiFetch(`/admin/users/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      loadUsers();
    } catch (err) { alert('Failed to update user'); }
  }

  // Force logout user
  async function forceLogout(id) {
    if (!confirm('Force logout this user?')) return;
    try {
      await apiFetch(`/admin/users/${id}/session`, { method: 'DELETE' });
      alert('User logged out successfully');
    } catch (err) { alert('Failed to logout user'); }
  }

  // Save promotion
  async function savePromo() {
    try {
      const body = { ...promoForm };
      if (!body.expiresAt) delete body.expiresAt;
      if (!body.minDeposit) delete body.minDeposit;

      if (editingPromo) {
        await apiFetch(`/admin/promotions/${editingPromo._id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch('/admin/promotions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      setShowPromoForm(false);
      setEditingPromo(null);
      setPromoForm({ bookmaker: 'betway_bw', title: '', description: '', howToUse: '', promoType: 'welcome', value: '', minDeposit: '', expiresAt: '' });
      loadPromotions();
    } catch (err) { alert('Failed to save promotion'); }
  }

  // Delete promotion
  async function deletePromo(id) {
    if (!confirm('Delete this promotion?')) return;
    try {
      await apiFetch(`/admin/promotions/${id}`, { method: 'DELETE' });
      loadPromotions();
    } catch (err) { alert('Failed to delete promotion'); }
  }

  // Edit promotion
  function editPromo(promo) {
    setEditingPromo(promo);
    setPromoForm({
      bookmaker: promo.bookmaker,
      title: promo.title,
      description: promo.description,
      howToUse: promo.howToUse || '',
      promoType: promo.promoType || 'other',
      value: promo.value || '',
      minDeposit: promo.minDeposit || '',
      expiresAt: promo.expiresAt ? new Date(promo.expiresAt).toISOString().split('T')[0] : '',
    });
    setShowPromoForm(true);
  }

  if (user?.role !== 'admin') {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}>Access denied.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18 }}>
        ⚙️ Admin Panel
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)' }}>
        {['stats', 'users', 'promotions', 'parlays'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '9px 16px', fontFamily: 'var(--font-display)', fontWeight: 600,
            fontSize: 12, textTransform: 'uppercase', letterSpacing: .5,
            color: tab === t ? 'var(--accent)' : 'var(--text3)',
            background: 'transparent', border: 'none',
            borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
            cursor: 'pointer',
          }}>
            {t}
          </button>
        ))}
      </div>

      {/* STATS TAB */}
      {tab === 'stats' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
            <StatCard label="Total Users" value={stats?.users?.total} color="var(--accent)" />
            <StatCard label="Subscribed to Alerts" value={stats?.users?.subscribed} color="var(--success)" />
            <StatCard label="Free Plan" value={stats?.users?.free} color="var(--text3)" />
            <StatCard label="Pro Plan" value={stats?.users?.pro} color="var(--accent2)" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
            <StatCard label="Live Arbs" value={stats?.platform?.activeArbs} color="var(--success)" />
            <StatCard label="Value Bets" value={stats?.platform?.activeValueBets} color="var(--accent)" />
            <StatCard label="Sharp Moves" value={stats?.platform?.activeSharpMoves} color="var(--warning)" />
          </div>
          <button className="btn btn-outline" style={{ alignSelf: 'flex-start', fontSize: 12 }} onClick={loadStats}>
            ↺ Refresh Stats
          </button>
        </div>
      )}

      {/* USERS TAB */}
      {tab === 'users' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              type="text" placeholder="Search name or email..."
              value={userSearch} onChange={e => setUserSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200, fontSize: 14 }}
            />
            <select value={userPlan} onChange={e => setUserPlan(e.target.value)} style={{ fontSize: 13 }}>
              <option value="ALL">All Plans</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="elite">Elite</option>
            </select>
            <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={loadUsers}>Search</button>
          </div>

          <div style={{ fontSize: 12, color: 'var(--text3)' }}>{userTotal} users found</div>

          <div className="card" style={{ overflow: 'hidden' }}>
            {loading ? (
              <div style={{ padding: 20 }}>
                {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8 }} />)}
              </div>
            ) : users.length === 0 ? (
              <div className="empty-state"><p>No users found.</p></div>
            ) : (
              <div>
                {users.map(u => (
                  <div key={u._id} style={{
                    padding: '12px 16px', borderBottom: '1px solid var(--border)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.email}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                        Joined: {new Date(u.createdAt).toLocaleDateString()} 
                        {u.lastLogin && ` · Last login: ${new Date(u.lastLogin).toLocaleDateString()}`}
                        {u.alertsEnabled && ' · 🔔 Alerts on'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
  value={u.plan}
  onChange={e => updateUser(u._id, { plan: e.target.value })}
  style={{ fontSize: 11, padding: '4px 8px' }}
>
  <option value="free">Free</option>
  <option value="pro">Pro</option>
  <option value="elite">Elite</option>
</select>
{user?.superAdmin && u._id !== user?._id && (
  <select
    value={u.role || 'user'}
    onChange={e => updateUser(u._id, { role: e.target.value })}
    style={{ fontSize: 11, padding: '4px 8px' }}
  >
    <option value="user">User</option>
    <option value="admin">Admin</option>
  </select>
)}
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 10, padding: '4px 8px', color: 'var(--danger)' }}
                        onClick={() => forceLogout(u._id)}
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* PROMOTIONS TAB */}
      {tab === 'promotions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button className="btn btn-primary" style={{ alignSelf: 'flex-start', fontSize: 13 }}
            onClick={() => { setShowPromoForm(true); setEditingPromo(null); }}>
            + Add Promotion
          </button>

          {/* Promo Form */}
          {showPromoForm && (
            <div className="card" style={{ padding: '20px 24px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
                {editingPromo ? 'Edit Promotion' : 'Add Promotion'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Bookmaker</label>
                  <select value={promoForm.bookmaker} onChange={e => setPromoForm(f => ({ ...f, bookmaker: e.target.value }))} style={{ width: '100%' }}>
                    {Object.entries(BOOKMAKER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Promo Type</label>
                  <select value={promoForm.promoType} onChange={e => setPromoForm(f => ({ ...f, promoType: e.target.value }))} style={{ width: '100%' }}>
                    {Object.entries(PROMO_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Title</label>
                  <input type="text" value={promoForm.title} onChange={e => setPromoForm(f => ({ ...f, title: e.target.value }))} style={{ width: '100%' }} placeholder="e.g. Welcome Bonus — Get P1000 free bet" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Description</label>
                  <textarea value={promoForm.description} onChange={e => setPromoForm(f => ({ ...f, description: e.target.value }))} style={{ width: '100%', minHeight: 80, resize: 'vertical' }} placeholder="Describe the promotion..." />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>How to use profitably</label>
                  <textarea value={promoForm.howToUse} onChange={e => setPromoForm(f => ({ ...f, howToUse: e.target.value }))} style={{ width: '100%', minHeight: 80, resize: 'vertical' }} placeholder="Step by step instructions..." />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Value (e.g. P1000 free bet)</label>
                  <input type="text" value={promoForm.value} onChange={e => setPromoForm(f => ({ ...f, value: e.target.value }))} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Expiry Date</label>
                  <input type="date" value={promoForm.expiresAt} onChange={e => setPromoForm(f => ({ ...f, expiresAt: e.target.value }))} style={{ width: '100%' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={savePromo}>
                  {editingPromo ? 'Update' : 'Save'} Promotion
                </button>
                <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={() => { setShowPromoForm(false); setEditingPromo(null); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Promotions list */}
          {promotions.length === 0 ? (
            <div className="empty-state"><p>No promotions yet. Add your first one!</p></div>
          ) : (
            <div className="card" style={{ overflow: 'hidden' }}>
              {promotions.map(promo => (
                <div key={promo._id} style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{promo.title}</span>
                        <span className="pill pill-teal" style={{ fontSize: 9 }}>{BOOKMAKER_LABELS[promo.bookmaker]}</span>
                        <span className="pill pill-blue" style={{ fontSize: 9 }}>{PROMO_TYPES[promo.promoType]}</span>
                        {promo.value && <span className="pill pill-green" style={{ fontSize: 9 }}>{promo.value}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>{promo.description}</div>
                      {promo.expiresAt && (
                        <div style={{ fontSize: 11, color: new Date(promo.expiresAt) < new Date() ? 'var(--danger)' : 'var(--text3)' }}>
                          {new Date(promo.expiresAt) < new Date() ? '⚠️ EXPIRED — ' : 'Expires: '}
                          {new Date(promo.expiresAt).toLocaleDateString('en-BW', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => editPromo(promo)}>Edit</button>
                      <button className="btn btn-ghost" style={{ fontSize: 11, color: 'var(--danger)' }} onClick={() => deletePromo(promo._id)}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
{/* PARLAYS TAB */}
      {tab === 'parlays' && (
        <DailyParlayAdmin />
      )}

    </div>
  );
}